import React, { useState, useEffect } from 'react';
import { FileText, AlertCircle, Upload, Eye, CheckCircle2, Clock, CalendarDays, X } from 'lucide-react';
import poeService from '../../../services/poeService';
import POEUploadModal from './POEUploadModal';
import { useAuth } from '../../../contexts/AuthContext';

const StudentPOEDashboard = () => {
  const { profile } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadAssessmentId, setUploadAssessmentId] = useState('');
  const [uploadAssessmentItem, setUploadAssessmentItem] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await poeService.getStudentDashboardItems();
      setItems(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load POE units.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [profile?.id]);

  const handleUpload = async (payload) => {
    await poeService.uploadMarkedScript(payload);
    await loadData();
  };

  const viewFile = async (item) => {
    try {
      const url = await poeService.getFilePreviewUrl(item.file_path);
      if (!url) throw new Error('File could not be opened.');
      setPreviewUrl(url);
      setPreviewTitle(item.file_name || `${item.unit_code || 'POE'} · CAM ${item.assessment_number || ''}`);
    } catch (err) {
      setError(err?.message || 'The evidence file could not be opened.');
    }
  };

  const closePreview = () => {
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewTitle('');
  };

  const getStatusBadge = (item) => {
    if (!item.assessment_id) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
          <Clock className="h-3 w-3" />
          Awaiting CAM
        </span>
      );
    }
    
    if (!item.evidence_id || item.workflow_stage === 'rejected') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
          <AlertCircle className="h-3 w-3" />
          {item.workflow_stage === 'rejected' ? 'Action Required: Returned' : 'Action Required: Upload POE'}
        </span>
      );
    }

    if (item.workflow_stage === 'approved') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700">
          <CheckCircle2 className="h-3 w-3" />
          POE Approved
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
        <Clock className="h-3 w-3" />
        Under Review
      </span>
    );
  };

  // Group data to form a pivot table: Rows = Units, Columns = CAMs
  const uniqueCAMs = Array.from(
    new Set(items.map((i) => i.assessment_title).filter(Boolean))
  ).sort((a, b) => {
    const numA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
    const numB = parseInt(b.match(/\d+/)?.[0] || '0', 10);
    if (numA !== numB) return numA - numB;
    return a.localeCompare(b);
  });

  const groupedUnits = Object.values(
    items.reduce((acc, item) => {
      if (!acc[item.unit_id]) {
        acc[item.unit_id] = {
          unit_id: item.unit_id,
          unit_code: item.unit_code,
          unit_name: item.unit_name,
          programme_name: item.programme_name,
          cams: {},
        };
      }
      if (item.assessment_title) {
        // Only map it if there is a real assessment, or if it's the default 'CAM 1' placeholder
        // Prefer timelines with actual evidence first, then timelines that have marks (can_upload), then anything else.
        const existing = acc[item.unit_id].cams[item.assessment_title];
        if (!existing || item.evidence_id || (!existing.evidence_id && item.can_upload)) {
          acc[item.unit_id].cams[item.assessment_title] = item;
        }
      }
      return acc;
    }, {})
  ).sort((a, b) => a.unit_code.localeCompare(b.unit_code));

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-blue-600"></div>
      </div>
    );
  }

  const renderCellContent = (item) => {
    if (!item) return null;

    // 1. If they already uploaded, show the status badge
    if (item.evidence_id && item.workflow_stage !== 'rejected') {
      return (
        <div className="flex flex-col gap-1.5">
          <button
            onClick={() => viewFile(item)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50"
          >
            <Eye className="h-3.5 w-3.5" />
            View File
          </button>
          {getStatusBadge(item)}
        </div>
      );
    }

    // 2. If no valid upload exists, check if deadline is passed
    const isPastDeadline = item.submission_deadline && new Date(item.submission_deadline) < new Date();
    if (isPastDeadline) {
      return (
        <span className="inline-flex items-center justify-center gap-1 w-full rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs font-semibold text-red-600 shadow-sm" title={`Deadline was: ${item.submission_deadline}`}>
          <X className="h-3.5 w-3.5" />
          Closed
        </span>
      );
    }

    // 3. If deadline is active but no mark yet
    if (!item.assessment_id) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-500">
          <Clock className="h-3 w-3" />
          Awaiting CAM
        </span>
      );
    }

    // 4. If deadline is active and mark exists
    if (item.can_upload) {
      return (
        <div className="flex flex-col items-center gap-1 w-full">
          <button
            onClick={() => {
              setUploadAssessmentId(item.assessment_id);
              setUploadAssessmentItem(item);
              setUploadOpen(true);
            }}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-blue-700"
          >
            <Upload className="h-3.5 w-3.5" />
            Upload POE
          </button>
          <span className="text-[9px] text-slate-400">
            DL: {item.submission_deadline ? String(item.submission_deadline) : 'None'}
          </span>
        </div>
      );
    }

    // Fallback
    return getStatusBadge(item);
  };


  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-200 bg-emerald-700 px-6 py-4">
          <h2 className="text-lg font-bold text-white">Registered Units & POE Status</h2>
          <p className="text-sm text-emerald-100/90">
            Upload your marked scripts (CAMs) for your registered units below.
          </p>
        </div>

        {error && (
          <div className="m-4 flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-700">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {groupedUnits.length === 0 && !error ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-slate-300" />
            <h3 className="mt-4 text-sm font-semibold text-slate-900">No registered units found</h3>
            <p className="mt-1 text-sm text-slate-500">You do not currently have any active unit registrations for the semester.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-emerald-700">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-white">#</th>
                  <th className="px-6 py-4 text-left font-semibold text-white w-1/4">Unit Code</th>
                  <th className="px-6 py-4 text-left font-semibold text-white w-1/3">Unit Name</th>
                  {uniqueCAMs.map((cam) => (
                    <th key={cam} className="px-6 py-4 text-center font-semibold text-white">
                      {cam}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {groupedUnits.map((row, index) => (
                  <tr key={row.unit_id} className="hover:bg-slate-50 transition-colors">
                    <td className="whitespace-nowrap px-6 py-6 text-slate-500">{index + 1}</td>
                    <td className="whitespace-nowrap px-6 py-6 font-bold text-slate-900">
                      {row.unit_code}
                    </td>
                    <td className="px-6 py-6 text-slate-600">
                      <div className="font-medium text-slate-900">{row.unit_name}</div>
                    </td>
                    {uniqueCAMs.map((cam) => {
                      const item = row.cams[cam];
                      return (
                        <td key={cam} className="px-6 py-6 text-center align-middle">
                          <div className="mx-auto w-32">
                            {renderCellContent(item)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <POEUploadModal
        isOpen={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setUploadAssessmentItem(null);
        }}
        onSubmit={handleUpload}
        initialAssessmentId={uploadAssessmentId}
        assessments={uploadAssessmentItem ? [uploadAssessmentItem] : []}
      />
      {previewUrl && (
        <div className="fixed inset-0 z-[1150] flex flex-col bg-slate-950/80 p-4 backdrop-blur-sm sm:p-6">
          <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h2 className="min-w-0 truncate text-sm font-semibold text-slate-900">{previewTitle}</h2>
              <button type="button" onClick={closePreview} className="ml-4 rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close file preview">×</button>
            </div>
            <iframe src={previewUrl} title="Uploaded POE file preview" className="min-h-0 flex-1 bg-slate-100" />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentPOEDashboard;
